import { useEffect, useRef } from "react";
import {
    Calendar,
    ArrowRight,
    Check,
    Clock,
    Sparkles,
    MapPin,
    Map as MapIcon,
    ClipboardCheck,
    AlertTriangle,
    Receipt,
    KeyRound,
    BarChart3,
} from "lucide-react";
import Footer from "../components/Footer";
import { trackBookDemoClick } from "../lib/api";
import { readMarketingConsent, readMetaCookies } from "../lib/consent";

const BOOKING_URL =
    "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ10rXkOUohcCNXlxaCSWd4tpElmhfNeKTZAoE5R-sdFgZjdZ86a39LqtgIK1QFQujWMvCNx1wXl";

const FAIR_DATE = "30. sep.–1. okt. 2026 · OCC · Odense";

function generateEventId() {
    const ts = Math.floor(Date.now() / 1000);
    const rand = Math.random().toString(36).slice(2, 6).padEnd(4, "0");
    return `book_${ts}_${rand}`;
}

/**
 * MesseLandingPage — dedicated campaign page for Rengøringsmessen 2026.
 * Reached via paid ads. Direct-to-Google-Calendar booking.
 * Fires Meta CAPI Lead + dataLayer event on CTA click for ad attribution.
 * noindex — do not surface in search.
 */
export default function MesseLandingPage() {
    const stickyRef = useRef(null);

    useEffect(() => {
        document.title = "Mød Cleany på Rengøringsmessen 2026 · Book 15 min.";
        const robots =
            document.querySelector("meta[name='robots']") ||
            (() => {
                const m = document.createElement("meta");
                m.name = "robots";
                document.head.appendChild(m);
                return m;
            })();
        robots.setAttribute("content", "noindex, nofollow");
        return () => robots.setAttribute("content", "index, follow");
    }, []);

    const handleBookClick = (source) => {
        const eventId = generateEventId();
        const marketingConsent = readMarketingConsent();
        const { fbp, fbc } = readMetaCookies();

        // Browser-side pixel (via GTM/dataLayer) — dedupes with server event on Meta.
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "book_demo_click",
            event_id: eventId,
            source: `messe-2026:${source}`,
        });

        // Server-side (Meta CAPI Lead + internal log) — fire-and-forget.
        trackBookDemoClick({
            event_id: eventId,
            marketing_consent: marketingConsent,
            fbp,
            fbc,
            source: "messe-2026",
        });

        // Open Calendar in a new tab — carries the same event_id so we can
        // stitch Calendar's booking back to the ad click if needed.
        const separator = BOOKING_URL.includes("?") ? "&" : "?";
        const url = `${BOOKING_URL}${separator}utm_source=cleany&utm_medium=landing&utm_campaign=messe-2026&utm_content=${encodeURIComponent(source)}&cleany_eid=${eventId}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="min-h-screen bg-[#0b1a2e] text-white" data-testid="messe-landing-page">
            {/* ============================================================== */}
            {/* HERO — Navy + mint accent, matches ad #1                        */}
            {/* ============================================================== */}
            <section className="relative overflow-hidden pt-8 pb-16 md:pt-12 md:pb-24">
                {/* Ambient background */}
                <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#0b1a2e] via-[#0e2140] to-[#0b1a2e]" />
                <div className="absolute -top-32 -right-32 -z-10 h-96 w-96 rounded-full bg-emerald-300/10 blur-3xl" />
                <div className="absolute top-1/3 -left-40 -z-10 h-96 w-96 rounded-full bg-sky-400/10 blur-3xl" />

                <div className="max-w-6xl mx-auto px-6 lg:px-10">
                    {/* Co-brand card */}
                    <div className="mx-auto mb-10 flex max-w-fit items-center gap-5 rounded-2xl bg-white px-6 py-3 shadow-2xl shadow-black/20 md:gap-8 md:px-8">
                        <div className="flex items-center gap-2">
                            <div className="grid h-8 w-8 place-items-center rounded-full bg-sky-100">
                                <Clock className="h-5 w-5 text-sky-600" strokeWidth={2.5} />
                            </div>
                            <span className="font-display text-2xl font-bold tracking-tight text-sky-600">
                                Cleany
                            </span>
                        </div>
                        <div className="h-8 w-px bg-slate-200" />
                        <div className="flex items-center gap-2">
                            <svg viewBox="0 0 24 30" className="h-8 w-6 shrink-0" aria-hidden>
                                <defs>
                                    <linearGradient id="dropGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#4bb0ff" />
                                        <stop offset="100%" stopColor="#0f4c9c" />
                                    </linearGradient>
                                </defs>
                                <path
                                    d="M12 1 C12 1 3 12 3 19 a9 9 0 0 0 18 0 C21 12 12 1 12 1 Z"
                                    fill="url(#dropGrad)"
                                />
                            </svg>
                            <div className="leading-tight">
                                <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
                                    Rengøringsmessen
                                </div>
                                <div className="font-display text-xl font-black tracking-tight text-slate-900">
                                    RENT I DANMARK
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Date pill */}
                    <div className="mx-auto mb-12 flex max-w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-2 text-sm font-semibold text-white/90 backdrop-blur">
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                        </span>
                        {FAIR_DATE}
                    </div>

                    {/* Headline */}
                    <div className="grid items-center gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-7">
                            <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                                Skal du på{" "}
                                <span className="text-emerald-300">Rengørings&shy;messen?</span>
                            </h1>
                            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">
                                Book 15 minutter med Cleany på standen — vi viser dig, hvordan
                                du styrer opgaver, medarbejdere og kvalitet uden Excel og
                                sedler i lommen.
                            </p>

                            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                                <button
                                    type="button"
                                    onClick={() => handleBookClick("hero")}
                                    data-testid="messe-cta-hero"
                                    className="group inline-flex items-center justify-center gap-3 rounded-full bg-white px-7 h-14 text-base font-semibold text-slate-900 shadow-2xl shadow-emerald-500/20 transition-all hover:shadow-emerald-400/30 hover:-translate-y-0.5 active:translate-y-0"
                                >
                                    <Calendar className="h-5 w-5 text-sky-600" />
                                    Book 15 min. på messen
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </button>
                                <a
                                    href="#hvad-faar-du"
                                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-transparent px-6 h-14 text-sm font-semibold text-white/80 transition-colors hover:border-white/40 hover:text-white"
                                    data-testid="messe-cta-scroll"
                                >
                                    Hvad får du?
                                </a>
                            </div>

                            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/60">
                                <div className="flex items-center gap-1.5">
                                    <MapPin className="h-3.5 w-3.5" />
                                    OCC · Odense
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Clock className="h-3.5 w-3.5" />
                                    15 minutter
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Sparkles className="h-3.5 w-3.5 text-emerald-300" />
                                    Uforpligtende
                                </div>
                            </div>
                        </div>

                        {/* Right column — big "15 min" visual */}
                        <div className="lg:col-span-5">
                            <div className="relative mx-auto max-w-sm">
                                <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-gradient-to-br from-emerald-400/20 via-sky-400/10 to-transparent blur-2xl" />
                                <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm">
                                    <div className="text-center">
                                        <div className="mx-auto grid h-40 w-40 place-items-center rounded-full bg-emerald-300 shadow-2xl shadow-emerald-500/30">
                                            <div className="text-center">
                                                <div className="font-display text-6xl font-black text-slate-900 leading-none">
                                                    15
                                                </div>
                                                <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-800">
                                                    minutter
                                                </div>
                                            </div>
                                        </div>
                                        <p className="mt-6 text-lg font-semibold text-white">
                                            Så viser vi dig Cleany live
                                        </p>
                                        <p className="mt-2 text-sm text-white/60">
                                            Skræddersyet til jeres drift — ingen skabelon-demo.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============================================================== */}
            {/* HVAD KAN CLEANY — hurtig feature-intro                          */}
            {/* ============================================================== */}
            <section className="border-t border-white/5 bg-black/10 py-16 md:py-20">
                <div className="mx-auto max-w-6xl px-6 lg:px-10">
                    <div className="mx-auto max-w-2xl text-center">
                        <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-emerald-300">
                            Kort intro
                        </p>
                        <h2 className="font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
                            Alt driften skal bruge — samlet ét sted
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-white/70">
                            Cleany er en dansk driftsplatform bygget til rengøringsfirmaer.
                            Her er en hurtig smagsprøve — resten viser vi dig på standen.
                        </p>
                    </div>

                    <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {[
                            {
                                icon: MapIcon,
                                title: "Kørsel & rutekort",
                                body: "Live-kort over dagens opgaver, GPS-tracking pr. medarbejder og præcis registrering af kilometer.",
                            },
                            {
                                icon: Calendar,
                                title: "Kalender & opgaver",
                                body: "Alle abonnementer og ad hoc-opgaver i én kalender. Tildel medarbejdere og se status live.",
                            },
                            {
                                icon: ClipboardCheck,
                                title: "Besøg & kvalitet",
                                body: "Kvalitetstjek og kundebesøg med tjeklister, foto-dokumentation og automatiske rapporter.",
                            },
                            {
                                icon: AlertTriangle,
                                title: "Reklamationer",
                                body: "Modtag, tildel og luk reklamationer med fuld sporbarhed — plus statistik der peger på mønstre.",
                            },
                            {
                                icon: Receipt,
                                title: "Fakturering",
                                body: "Fakturér direkte fra opgaver. Send med ét klik til e-conomic eller Dinero — ingen dobbeltarbejde.",
                            },
                            {
                                icon: KeyRound,
                                title: "Nøgledatabase",
                                body: "Styr på hvem der har hvilken nøgle til hvilken kunde — med udleverings- og returneringslog.",
                            },
                        ].map((f) => (
                            <div
                                key={f.title}
                                className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-all hover:-translate-y-0.5 hover:border-emerald-300/30 hover:bg-white/[0.06]"
                                data-testid={`messe-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            >
                                <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-300/10 text-emerald-300 transition-colors group-hover:bg-emerald-300/15">
                                    <f.icon className="h-5 w-5" strokeWidth={2} />
                                </div>
                                <h3 className="mt-5 font-display text-lg font-bold text-white">
                                    {f.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-white/65">
                                    {f.body}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-white/50">
                        <div className="flex items-center gap-1.5">
                            <BarChart3 className="h-3.5 w-3.5" />
                            Statistikker & KPI-dashboards
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Sparkles className="h-3.5 w-3.5" />
                            Self-billing til underleverandører
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Check className="h-3.5 w-3.5" />
                            Dansk support · Data i EU
                        </div>
                    </div>
                </div>
            </section>

            {/* ============================================================== */}
            {/* 2 DAGE vs 15 MIN. — comparison strip (matches ad #5)            */}
            {/* ============================================================== */}
            <section className="border-y border-white/5 bg-black/20 py-16">
                <div className="mx-auto max-w-5xl px-6 lg:px-10">
                    <p className="mb-6 text-center text-xs font-bold uppercase tracking-[0.3em] text-emerald-300">
                        Hurtigt overblik
                    </p>
                    <h2 className="mx-auto max-w-3xl text-center font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
                        Messen varer 2 dage.<br />
                        Din gennemgang tager 15 min.
                    </h2>
                    <div className="mx-auto mt-12 grid max-w-3xl gap-4 sm:grid-cols-2">
                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">
                                Messen varer
                            </div>
                            <div className="mt-3 flex items-baseline gap-3">
                                <div className="font-display text-6xl font-black leading-none">2</div>
                                <div className="text-lg font-semibold text-white/70">dage</div>
                            </div>
                        </div>
                        <div className="rounded-2xl bg-emerald-300 p-7 text-slate-900 shadow-2xl shadow-emerald-500/20">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-800/80">
                                Cleany tager
                            </div>
                            <div className="mt-3 flex items-baseline gap-3">
                                <div className="font-display text-6xl font-black leading-none">15</div>
                                <div className="text-lg font-semibold text-slate-800/90">min.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============================================================== */}
            {/* EXCEL-ROD → CLEANY-OVERBLIK (matches ad #4)                     */}
            {/* ============================================================== */}
            <section className="py-20">
                <div className="mx-auto max-w-5xl px-6 lg:px-10">
                    <div className="grid items-center gap-10 lg:grid-cols-2">
                        <div>
                            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
                                Skal din drift stadig styres i Excel{" "}
                                <span className="text-emerald-300">efter messen?</span>
                            </h2>
                            <p className="mt-5 text-base leading-relaxed text-white/70">
                                Cleany samler ruteplaner, kvalitetstjek, timeregistrering og
                                kundekommunikation i ét system — så du slipper for at hoppe
                                mellem Excel, Messenger og en notesbog.
                            </p>
                            <button
                                type="button"
                                onClick={() => handleBookClick("excel-section")}
                                data-testid="messe-cta-excel"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-300 px-6 h-12 text-sm font-bold text-slate-900 shadow-lg shadow-emerald-500/20 transition-all hover:-translate-y-0.5"
                            >
                                Se alternativet på 15 min.
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                            <div className="rounded-full border-2 border-rose-300/40 bg-rose-500/10 px-6 py-3 text-sm font-bold uppercase tracking-wider text-rose-200">
                                Excel-rod
                            </div>
                            <ArrowRight className="h-6 w-6 text-white/40 sm:h-8 sm:w-8" />
                            <div className="rounded-full bg-emerald-300 px-6 py-3 text-sm font-bold uppercase tracking-wider text-slate-900 shadow-lg shadow-emerald-500/20">
                                Cleany-overblik
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============================================================== */}
            {/* HVAD FÅR DU — 4 bullets                                          */}
            {/* ============================================================== */}
            <section id="hvad-faar-du" className="border-t border-white/5 bg-black/20 py-20 scroll-mt-8">
                <div className="mx-auto max-w-5xl px-6 lg:px-10">
                    <div className="mx-auto max-w-2xl text-center">
                        <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-emerald-300">
                            Live på messen
                        </p>
                        <h2 className="font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
                            Mød Cleany på Rengøringsmessen
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-white/70">
                            15 minutter kan gøre en større forskel, end du tror. Her er hvad
                            du får med hjem fra vores stand.
                        </p>
                    </div>

                    <div className="mt-12 grid gap-5 sm:grid-cols-2">
                        {[
                            {
                                title: "Live gennemgang af jeres drift",
                                body: "Vi taler ud fra jeres virkelighed — antal medarbejdere, kunder og opgavetyper — og viser hvordan Cleany ville køre hos jer.",
                            },
                            {
                                title: "Konkret prisestimat med hjem",
                                body: "Du ved præcis hvad Cleany koster for jer, inden du forlader standen. Ingen “vi vender tilbage”.",
                            },
                            {
                                title: "Svar på Excel-udfordringen",
                                body: "Sedler, dobbeltarbejde, glemte tjek. Vi viser konkret hvordan andre firmaer har afskaffet det.",
                            },
                            {
                                title: "Ingen forpligtelser — ingen sælgersnak",
                                body: "Vi lover ikke at ringe dig ned bagefter. Hvis Cleany ikke passer jer, siger vi det ærligt.",
                            },
                        ].map((it) => (
                            <div
                                key={it.title}
                                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-emerald-300/30 hover:bg-white/[0.05]"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-300 text-slate-900">
                                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-base font-bold">
                                            {it.title}
                                        </h3>
                                        <p className="mt-2 text-sm leading-relaxed text-white/65">
                                            {it.body}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================================== */}
            {/* FINAL CTA                                                        */}
            {/* ============================================================== */}
            <section className="py-20">
                <div className="mx-auto max-w-3xl px-6 lg:px-10 text-center">
                    <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-emerald-300">
                        {FAIR_DATE}
                    </p>
                    <h2 className="font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
                        Har du 15 minutter på messen?
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-white/70">
                        Så viser vi dig Cleany live. Book din tid nu — pladserne er
                        begrænsede, og de gode tider ryger først.
                    </p>
                    <button
                        type="button"
                        onClick={() => handleBookClick("final")}
                        data-testid="messe-cta-final"
                        className="group mt-8 inline-flex items-center gap-3 rounded-full bg-emerald-300 px-8 h-14 text-base font-bold text-slate-900 shadow-2xl shadow-emerald-500/30 transition-all hover:-translate-y-0.5 hover:shadow-emerald-400/40"
                    >
                        <Calendar className="h-5 w-5" />
                        Book 15 min. på messen
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </button>
                    <p className="mt-5 text-xs text-white/50">
                        Du sendes til vores kalender — bekræftelse på mail med det samme.
                    </p>
                </div>
            </section>

            <Footer />

            {/* ============================================================== */}
            {/* MOBILE STICKY CTA                                                */}
            {/* ============================================================== */}
            <div
                ref={stickyRef}
                className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-[#0b1a2e]/95 px-4 py-3 backdrop-blur-md md:hidden"
            >
                <button
                    type="button"
                    onClick={() => handleBookClick("mobile-sticky")}
                    data-testid="messe-cta-sticky"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 h-12 text-sm font-bold text-slate-900 shadow-lg shadow-emerald-500/20 active:scale-[0.98]"
                >
                    <Calendar className="h-4 w-4" />
                    Book 15 min. på messen
                    <ArrowRight className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
